import React, { useState, useEffect } from 'react';
import APP_COLOURS from '../../config/colors';
import StaffAttendance from './StaffProfile/StaffAttendance';
import StaffSales from './StaffProfile/StaffSales';
import StaffCustomers from './StaffProfile/StaffCustomers';
import StaffProductivitySheets from './StaffProfile/StaffProductivitySheets';
import StaffChangeLogs from './StaffProfile/StaffChangeLogs';
import { managerAPI } from '../../config/api';
import { getAvatarImage } from '../../config/avatars';
import moment from 'moment';

const StaffProfile = ({ selectedStaff, setSelectedStaff, managerId, onAllocationChange }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isAllocating, setIsAllocating] = useState(false);

  if (!selectedStaff) return null;

  // Check if staff is allocated to this manager
  const isAllocated = selectedStaff.manager?.some(m => m.id === managerId) || false;

  // Only show overview tab if not allocated
  const tabs = isAllocated ? [
    { id: 'overview', label: 'Overview', icon: 'fa-user' },
    { id: 'attendance', label: 'Attendance', icon: 'fa-calendar-check' },
    { id: 'sales', label: 'Sales', icon: 'fa-chart-line' },
    { id: 'customers', label: 'Customers', icon: 'fa-users' },
    { id: 'productivity', label: 'Productivity Sheets', icon: 'fa-chart-bar' },
    { id: 'changelog', label: 'Change Log', icon: 'fa-history' },
  ] : [
    { id: 'overview', label: 'Overview', icon: 'fa-user' },
  ];

  return (
    <div className="rounded-lg overflow-hidden animate-slide-in" style={{maxWidth: '1200px', margin: '0 auto'}}>
      {/* Header with back button */}
      <div className="p-2 md:p-6" style={{ borderBottom: `1px solid ${APP_COLOURS.BG4}` }}>
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => setSelectedStaff(null)}
            className="flex items-center px-2 py-1.5 md:px-3 md:py-2 rounded transition-all duration-200 text-sm"
            style={{ 
              backgroundColor: APP_COLOURS.BG4,
              color: APP_COLOURS.TEXTCOLOR,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = APP_COLOURS.BG5;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = APP_COLOURS.BG4;
            }}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back
          </button>
        </div>
        
        {/* Profile Header */}
        <div className="flex items-center mb-3 md:mb-6">
          <div className="h-14 w-14 md:h-20 md:w-20 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ border: `2px solid ${APP_COLOURS.BLUE}` }}>
            <img 
              src={getAvatarImage(selectedStaff.avatar || selectedStaff.avatar_id || '1')} 
              alt={`${selectedStaff.first_name} ${selectedStaff.last_name}`}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="ml-3 md:ml-6 min-w-0 flex-1">
            <h2 className="text-base md:text-2xl font-semibold truncate" style={{ color: APP_COLOURS.TEXTCOLOR }}>
              {selectedStaff.first_name} {selectedStaff.last_name}
            </h2>
            <p className="text-xs md:text-base mt-0.5" style={{ color: APP_COLOURS.PLACEHOLDER }}>
              {selectedStaff.position || 'No position assigned'}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
              <div className="flex items-center">
                <i className="fas fa-map-marker-alt text-xs mr-1.5" style={{ color: APP_COLOURS.BLUE }}></i>
                <span className="text-xs" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  {selectedStaff.mall?.title || selectedStaff.mall || 'Mall not set'}
                </span>
              </div>
              <div className="flex items-center">
                <i className="fas fa-store text-xs mr-1.5" style={{ color: APP_COLOURS.BLUE }}></i>
                <span className="text-xs" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  {selectedStaff.retailer?.title || selectedStaff.retailer || 'Retailer not set'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex mt-3 md:mt-6" style={{ borderBottom: `1px solid ${APP_COLOURS.BG4}` }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex-1 py-2 md:py-3 font-medium text-xs md:text-sm transition-all duration-200 relative flex items-center justify-center"
              style={{
                color: activeTab === tab.id ? APP_COLOURS.BLUE : APP_COLOURS.PLACEHOLDER,
                borderBottom: activeTab === tab.id ? `2px solid ${APP_COLOURS.BLUE}` : '2px solid transparent',
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.color = APP_COLOURS.TEXTCOLOR;
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.color = APP_COLOURS.PLACEHOLDER;
                }
              }}
            >
              <i className={`fas ${tab.icon} md:mr-1.5`}></i>
              <span className="hidden md:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
      
      {/* Tab Content */}
      <div className="p-2 md:p-6">
        {activeTab === 'overview' && (
          <OverviewTab 
            selectedStaff={selectedStaff} 
            isAllocated={isAllocated}
            isAllocating={isAllocating}
            setIsAllocating={setIsAllocating}
            managerId={managerId}
            onAllocationChange={onAllocationChange}
          />
        )}
        {activeTab === 'attendance' && <AttendanceTab selectedStaff={selectedStaff} isAllocated={isAllocated} />}
        {activeTab === 'sales' && <SalesTab selectedStaff={selectedStaff} isAllocated={isAllocated} />}
        {activeTab === 'customers' && <CustomersTab selectedStaff={selectedStaff} isAllocated={isAllocated} />}
        {activeTab === 'productivity' && <StaffProductivitySheets selectedStaff={selectedStaff} />}
        {activeTab === 'changelog' && <ChangeLogTab selectedStaff={selectedStaff} isAllocated={isAllocated} />}
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ selectedStaff, isAllocated, isAllocating, setIsAllocating, managerId, onAllocationChange }) => {
  const [todayData, setTodayData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAllocated) {
      fetchTodayOverview();
    }
  }, [selectedStaff.id, isAllocated]);

  const fetchTodayOverview = async () => {
    try {
      setLoading(true);
      const data = await managerAPI.getStaffTodayOverview(selectedStaff.id);
      console.log('=== FRONTEND: TODAY DATA DEBUG ===');
      console.log('Full response:', JSON.stringify(data, null, 2));
      console.log('Attendance data:', data.attendance);
      if (data.attendance) {
        console.log('Attendance fields:', Object.keys(data.attendance));
        console.log('clockIn:', data.attendance.clockIn);
        console.log('clockOut:', data.attendance.clockOut);
        console.log('lunch_start:', data.attendance.lunch_start);
        console.log('lunch_end:', data.attendance.lunch_end);
      }
      setTodayData(data);
    } catch (error) {
      console.error('Error fetching today overview:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-6">
      {/* LEFT COLUMN - Staff Details */}
      <div className="space-y-3 md:space-y-6">
        {/* Contact Information */}
        <div className="p-3 md:p-6 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
          <h3 className="text-sm md:text-lg font-semibold mb-3" style={{ color: APP_COLOURS.TEXTCOLOR }}>
            <i className="fas fa-address-card mr-2" style={{ color: APP_COLOURS.BLUE }}></i>
            Contact Information
          </h3>
          <div className="space-y-3">
            <div className="flex items-start">
              <i className="fas fa-envelope text-sm mt-0.5 mr-3" style={{ color: APP_COLOURS.BLUE, width: '16px' }}></i>
              <div>
                <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>Email Address</p>
                <p className="text-sm font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>{selectedStaff.email || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start">
              <i className="fas fa-phone text-sm mt-0.5 mr-3" style={{ color: APP_COLOURS.BLUE, width: '16px' }}></i>
              <div>
                <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>Phone Number</p>
                <p className="text-sm font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>{selectedStaff.phone || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Assigned Brands */}
        <div className="p-3 md:p-6 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
          <h3 className="text-sm md:text-lg font-semibold mb-3" style={{ color: APP_COLOURS.TEXTCOLOR }}>
            <i className="fas fa-tag mr-2" style={{ color: APP_COLOURS.BLUE }}></i>
            Assigned Brands
          </h3>
          <div className="space-y-2">
            {selectedStaff.brands && selectedStaff.brands.length > 0 ? (
              selectedStaff.brands.map((brand, index) => {
                const brandTitle = typeof brand === 'object' ? brand.title : brand;
                const brandId = typeof brand === 'object' ? brand.id : index;
                const brandMall = typeof brand === 'object' ? (brand.mall?.title || brand.mall) : null;
                const brandRetailer = typeof brand === 'object' ? (brand.retailer?.title || brand.retailer) : null;
                return (
                  <div
                    key={brandId}
                    className="px-3 py-2 rounded-lg"
                    style={{ 
                      backgroundColor: 'rgba(0, 104, 255, 0.15)',
                      border: `1px solid rgba(0, 104, 255, 0.3)`
                    }}
                  >
                    <div className="flex items-start">
                      <i className="fas fa-tag text-xs mr-2 mt-0.5" style={{ color: APP_COLOURS.BLUE }}></i>
                      <div className="flex-1">
                        <span className="text-sm font-medium block" style={{ color: APP_COLOURS.BLUE }}>{brandTitle}</span>
                        {(brandMall || brandRetailer) && (
                          <div className="mt-1 space-y-0.5">
                            {brandMall && (
                              <div className="flex items-center">
                                <i className="fas fa-map-marker-alt text-xs mr-1.5" style={{ color: APP_COLOURS.BLUE, opacity: 0.7 }}></i>
                                <span className="text-xs" style={{ color: APP_COLOURS.TEXTCOLOR, opacity: 0.8 }}>
                                  {brandMall}
                                </span>
                              </div>
                            )}
                            {brandRetailer && (
                              <div className="flex items-center">
                                <i className="fas fa-store text-xs mr-1.5" style={{ color: APP_COLOURS.BLUE, opacity: 0.7 }}></i>
                                <span className="text-xs" style={{ color: APP_COLOURS.TEXTCOLOR, opacity: 0.8 }}>
                                  {brandRetailer}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>No brands assigned</p>
            )}
          </div>
        </div>

        {/* Additional Information */}
        <div className="p-3 md:p-6 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
          <h3 className="text-sm md:text-lg font-semibold mb-3" style={{ color: APP_COLOURS.TEXTCOLOR }}>
            <i className="fas fa-info-circle mr-2" style={{ color: APP_COLOURS.BLUE }}></i>
            Additional Information
          </h3>
          <div className="space-y-3">
            <div className="flex items-start">
              <i className="fas fa-id-badge text-sm mt-0.5 mr-3" style={{ color: APP_COLOURS.BLUE, width: '16px' }}></i>
              <div>
                <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>Staff ID</p>
                <p className="text-xs font-mono font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>{selectedStaff.id}</p>
              </div>
            </div>
            {selectedStaff.organisationID && (
              <div className="flex items-start">
                <i className="fas fa-building text-sm mt-0.5 mr-3" style={{ color: APP_COLOURS.BLUE, width: '16px' }}></i>
                <div>
                  <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>Organization</p>
                  <p className="text-sm font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>{selectedStaff.organisationID}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Allocation Status */}
        <div className="p-3 md:p-6 rounded-lg" style={{ 
          backgroundColor: isAllocated ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)', 
          border: `1px solid ${isAllocated ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}` 
        }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <i className={`fas ${isAllocated ? 'fa-check-circle' : 'fa-exclamation-circle'} mr-2`} 
                 style={{ color: isAllocated ? '#22c55e' : '#ef4444' }}></i>
              <span className="font-semibold text-sm md:text-lg" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                Allocation Status
              </span>
            </div>
            <span 
              className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded ${
                isAllocated ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
            >
              {isAllocated ? 'Allocated' : 'Not Allocated'}
            </span>
          </div>
          <p className="text-xs mb-3" style={{ color: APP_COLOURS.PLACEHOLDER }}>
            {isAllocated 
              ? 'You can view this staff member\'s data.' 
              : 'Allocate to view their sales and attendance.'}
          </p>
          <button
            onClick={async () => {
              setIsAllocating(true);
              try {
                if (isAllocated) {
                  await managerAPI.deallocateStaff(selectedStaff.id);
                } else {
                  await managerAPI.allocateStaff(selectedStaff.id);
                }
                // Notify parent to refresh staff list
                if (onAllocationChange) {
                  await onAllocationChange();
                }
              } catch (error) {
                console.error('Error toggling allocation:', error);
                alert(error.message || 'Failed to update allocation');
              } finally {
                setIsAllocating(false);
              }
            }}
            disabled={isAllocating}
            className="w-full px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center"
            style={{
              backgroundColor: isAllocated ? '#ef4444' : APP_COLOURS.BLUE,
              color: 'white',
              opacity: isAllocating ? 0.6 : 1,
              cursor: isAllocating ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={(e) => {
              if (!isAllocating) {
                e.currentTarget.style.opacity = '0.9';
              }
            }}
            onMouseLeave={(e) => {
              if (!isAllocating) {
                e.currentTarget.style.opacity = '1';
              }
            }}
          >
            {isAllocating ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                {isAllocated ? 'Deallocating...' : 'Allocating...'}
              </>
            ) : (
              <>
                <i className={`fas ${isAllocated ? 'fa-user-minus' : 'fa-user-plus'} mr-2`}></i>
                {isAllocated ? 'Deallocate' : 'Allocate to Me'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* RIGHT COLUMN - Today's Activity */}
      {isAllocated ? (
        <div className="space-y-3 md:space-y-6">
          <div className="rounded-lg">
            <h3 className="text-sm md:text-xl font-semibold mb-3" style={{ color: APP_COLOURS.TEXTCOLOR }}>
              <i className="fas fa-calendar-day mr-2" style={{ color: APP_COLOURS.BLUE }}></i>
              Today's Activity
            </h3>
            
            {loading ? (
              <div className="text-center py-6">
                <i className="fas fa-spinner fa-spin text-2xl" style={{ color: APP_COLOURS.BLUE }}></i>
              </div>
            ) : todayData ? (
              <div className="space-y-3">
                {/* Check-in Info */}
                <div className="p-3 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <i className="fas fa-clock text-sm mr-2" style={{ color: APP_COLOURS.BLUE }}></i>
                      <span className="font-semibold text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>Check-in</span>
                    </div>
                    <div className="flex items-center">
                      <i className={`fas fa-circle text-xs mr-1.5`} style={{ color: todayData.isOnlineToday ? '#22c55e' : APP_COLOURS.PLACEHOLDER }}></i>
                      <span className="text-xs" style={{ color: todayData.isOnlineToday ? '#22c55e' : APP_COLOURS.PLACEHOLDER }}>
                        {todayData.isOnlineToday ? 'Online' : 'Offline'}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs mb-0.5" style={{ color: APP_COLOURS.PLACEHOLDER }}>Clock In</p>
                      <p className="text-base font-bold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                        {todayData.attendance?.clockIn ? 
                          moment(todayData.attendance.clockIn).format("HH:mm")
                          : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs mb-0.5" style={{ color: APP_COLOURS.PLACEHOLDER }}>Clock Out</p>
                      <p className="text-base font-bold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                        {todayData.attendance?.clockOut ? 
                          moment(todayData.attendance.clockOut).format("HH:mm") 
                          : '-'}
                      </p>
                    </div>
                  </div>
                  {/* Lunch Times */}
                  {(todayData.attendance?.lunch_start || todayData.attendance?.lunch_end) && (
                    <div className="grid grid-cols-2 gap-3 mt-2 pt-2" style={{ borderTop: `1px solid ${APP_COLOURS.BG4}` }}>
                      <div>
                        <p className="text-xs mb-0.5" style={{ color: APP_COLOURS.PLACEHOLDER }}>Lunch Start</p>
                        <p className="text-sm font-bold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                          {todayData.attendance?.lunch_start ? 
                            moment(todayData.attendance.lunch_start).format("HH:mm")
                            : '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs mb-0.5" style={{ color: APP_COLOURS.PLACEHOLDER }}>Lunch End</p>
                        <p className="text-sm font-bold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                          {todayData.attendance?.lunch_end ? 
                            moment(todayData.attendance.lunch_end).format("HH:mm")
                            : '-'}
                        </p>
                      </div>
                    </div>
                  )}
                  {todayData.lastSeen && (
                    <p className="text-xs mt-1.5" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                      Last seen: {todayData.lastSeen}
                    </p>
                  )}
                </div>

                {/* Today's Sales Summary */}
                <div className="p-3 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <i className="fas fa-cash-register text-sm mr-2" style={{ color: APP_COLOURS.BLUE }}></i>
                      <span className="font-semibold text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>Today's Sales</span>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold" style={{ color: APP_COLOURS.BLUE }}>
                        R{todayData.totalSalesToday?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                        {todayData.salesCount || 0} transaction{todayData.salesCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>

                  {/* Sales List */}
                  {todayData.sales && todayData.sales.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {todayData.sales
                        .sort((a, b) => {
                          // Sort by dateCreated in descending order (latest first)
                          const dateA = a.dateCreated ? new Date(a.dateCreated).getTime() : 0;
                          const dateB = b.dateCreated ? new Date(b.dateCreated).getTime() : 0;
                          return dateB - dateA;
                        })
                        .map((sale, index) => {
                        // Get items from sale
                        const items = sale.items || [];
                        
                        return (
                          <div 
                            key={sale.id} 
                            className="p-3 rounded"
                            style={{ 
                              backgroundColor: APP_COLOURS.BG2, 
                              border: `1px solid ${APP_COLOURS.BG4}`
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                              <div style={{ display: 'flex', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
                                <div className="flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0" 
                                     style={{ backgroundColor: APP_COLOURS.BG4, marginRight: '12px' }}>
                                  <span className="text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                                    {index + 1}
                                  </span>
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                                      {sale.dateCreated ? moment(sale.dateCreated).format('h:mm A') : 'N/A'}
                                      {items.length > 0 && ` • ${items.length} item${items.length !== 1 ? 's' : ''}`}
                                    </p>
                                  </div>
                                  {items.length > 0 ? (
                                    <div className="space-y-1">
                                      {items.map((item, itemIndex) => {
                                        const itemName = item.name || item.title || item;
                                        return (
                                          <div key={itemIndex} className="flex items-center">
                                            <i className="fas fa-tag text-xs mr-2" style={{ color: APP_COLOURS.BLUE }}></i>
                                            <span className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                                              {itemName}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <p className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>No items</p>
                                  )}
                                </div>
                              </div>
                              <div style={{ flexShrink: 0, textAlign: 'right' }}>
                                <p className="text-sm font-bold" style={{ color: APP_COLOURS.BLUE, whiteSpace: 'nowrap' }}>
                                  R{parseFloat(sale.trs || 0).toFixed(2)}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-center py-4 text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                      No sales recorded today
                    </p>
                  )}
                </div>

                {/* Total Customers */}
                <div className="p-3 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <i className="fas fa-users text-sm mr-2" style={{ color: APP_COLOURS.BLUE }}></i>
                      <span className="font-semibold text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>Total Customers</span>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                        {todayData.totalCustomers || 0}
                      </p>
                      <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                        Owned by this staff
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-center py-8" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                Unable to load today's activity
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center p-8" style={{ backgroundColor: APP_COLOURS.BG3, borderRadius: '12px', border: `1px solid ${APP_COLOURS.BG4}` }}>
          <div className="text-center">
            <i className="fas fa-lock text-3xl mb-2" style={{ color: APP_COLOURS.PLACEHOLDER }}></i>
            <p className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>
              Allocate staff to view today's activity
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Attendance Tab Component
const AttendanceTab = ({ selectedStaff, isAllocated }) => {
  if (!isAllocated) {
    return (
      <div className="p-12 text-center" style={{ backgroundColor: APP_COLOURS.BG3, margin: '24px', borderRadius: '12px', border: `1px solid ${APP_COLOURS.BG4}` }}>
        <i className="fas fa-lock text-6xl mb-4" style={{ color: APP_COLOURS.PLACEHOLDER }}></i>
        <h3 className="text-xl font-semibold mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
          Attendance Data Not Available
        </h3>
        <p className="text-base" style={{ color: APP_COLOURS.PLACEHOLDER }}>
          You need to allocate this staff member to yourself to view their attendance data.
        </p>
        <p className="text-sm mt-2" style={{ color: APP_COLOURS.PLACEHOLDER }}>
          Go to the Overview tab to allocate them.
        </p>
      </div>
    );
  }

  return (
    <StaffAttendance 
      staffId={selectedStaff.id} 
      staffName={`${selectedStaff.first_name} ${selectedStaff.last_name}`}
    />
  );
};

// Sales Tab Component
const SalesTab = ({ selectedStaff, isAllocated }) => {
  if (!isAllocated) {
    return (
      <div className="p-12 text-center" style={{ backgroundColor: APP_COLOURS.BG3, margin: '24px', borderRadius: '12px', border: `1px solid ${APP_COLOURS.BG4}` }}>
        <i className="fas fa-lock text-6xl mb-4" style={{ color: APP_COLOURS.PLACEHOLDER }}></i>
        <h3 className="text-xl font-semibold mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
          Sales Data Not Available
        </h3>
        <p className="text-base" style={{ color: APP_COLOURS.PLACEHOLDER }}>
          You need to allocate this staff member to yourself to view their sales data.
        </p>
        <p className="text-sm mt-2" style={{ color: APP_COLOURS.PLACEHOLDER }}>
          Go to the Overview tab to allocate them.
        </p>
      </div>
    );
  }

  return (
    <StaffSales 
      staffId={selectedStaff.id} 
      staffName={`${selectedStaff.first_name} ${selectedStaff.last_name}`}
      staffData={selectedStaff}
    />
  );
};

// Customers Tab Component
const CustomersTab = ({ selectedStaff, isAllocated }) => {
  if (!isAllocated) {
    return (
      <div className="p-12 text-center" style={{ backgroundColor: APP_COLOURS.BG3, margin: '24px', borderRadius: '12px', border: `1px solid ${APP_COLOURS.BG4}` }}>
        <i className="fas fa-lock text-6xl mb-4" style={{ color: APP_COLOURS.PLACEHOLDER }}></i>
        <h3 className="text-xl font-semibold mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
          Customer Data Not Available
        </h3>
        <p className="text-base" style={{ color: APP_COLOURS.PLACEHOLDER }}>
          You need to allocate this staff member to yourself to view their customer data.
        </p>
        <p className="text-sm mt-2" style={{ color: APP_COLOURS.PLACEHOLDER }}>
          Go to the Overview tab to allocate them.
        </p>
      </div>
    );
  }

  return (
    <StaffCustomers 
      staffId={selectedStaff.id} 
      staffName={`${selectedStaff.first_name} ${selectedStaff.last_name}`}
    />
  );
};

// Change Log Tab Component
const ChangeLogTab = ({ selectedStaff, isAllocated }) => {
  if (!isAllocated) {
    return (
      <div className="p-12 text-center" style={{ backgroundColor: APP_COLOURS.BG3, margin: '24px', borderRadius: '12px', border: `1px solid ${APP_COLOURS.BG4}` }}>
        <i className="fas fa-lock text-6xl mb-4" style={{ color: APP_COLOURS.PLACEHOLDER }}></i>
        <h3 className="text-xl font-semibold mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
          Change Log Not Available
        </h3>
        <p className="text-base" style={{ color: APP_COLOURS.PLACEHOLDER }}>
          You need to allocate this staff member to yourself to view their change log.
        </p>
        <p className="text-sm mt-2" style={{ color: APP_COLOURS.PLACEHOLDER }}>
          Go to the Overview tab to allocate them.
        </p>
      </div>
    );
  }

  return (
    <StaffChangeLogs 
      staffId={selectedStaff.id} 
      staffName={`${selectedStaff.first_name} ${selectedStaff.last_name}`}
    />
  );
};

export default StaffProfile;

